import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, Mail, Building2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useApi, errMsg, fmtDate, imgUrl } from "@/lib/api";
import { EmptyState, PageHeader, Spinner } from "@/components/shared/common";
import { useCategories, CatIcon } from "@/pages/Home";

const Wrap = ({ children }) => <div className="container-x py-12">{children}</div>;

export function Categories() {
  const cats = useCategories();
  const groups = [...new Set(cats.map((c) => c.group))];
  return (
    <Wrap>
      <PageHeader eyebrow="Categories" title="Every discipline in software" text="Managed by the DevMarket team. Counts reflect live listings." />
      {groups.map((g) => (
        <div key={g} className="mt-10"><h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">{g}</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">{cats.filter((c) => c.group === g).map((c) => (
            <Link key={c.slug} to={g === "Digital Products" ? `/products?category=${c.slug}` : `/services?category=${c.slug}`} className="card-surface card-hover flex items-center gap-4 p-5" data-testid={`category-card-${c.slug}`}>
              <CatIcon name={c.icon} className="h-6 w-6 text-primary" /><div><p className="font-semibold">{c.name}</p><p className="text-xs text-muted-foreground">{c.service_count} services · {c.product_count} products</p></div>
            </Link>))}</div>
        </div>
      ))}
    </Wrap>
  );
}

export function Pricing() {
  const api = useApi();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings/public").then((r) => setS(r.data)); }, [api]);
  const pct = s?.commission_pct ?? "…";
  const rows = [["Buyers", "Free", ["No buyer service fee", "Pay securely in ₹ via cards, UPI & net banking", "Refund requests & disputes", "Verified reviews after completion"]],
    ["Sellers", `${pct}% commission`, [`${pct}% platform fee deducted from each completed sale`, "Unlimited service & product listings", "Customizable storefront", "Payout requests from your available balance"]],
    ["Agencies", `${pct}% commission`, ["Everything for sellers", "Invite team members with roles", "Shared listings and orders", "Team performance dashboard"]]];
  return (
    <Wrap>
      <PageHeader eyebrow="Pricing" title="Simple, disclosed fees" text="No hidden charges. Fees are calculated on our servers and shown before you pay." />
      <div className="mt-10 grid gap-6 md:grid-cols-3">{rows.map(([t, price, feats], i) => (
        <div key={t} className={`card-surface p-8 ${i === 1 ? "ring-2 ring-primary" : ""}`} data-testid={`pricing-card-${t.toLowerCase()}`}>
          <h3 className="text-lg font-semibold">{t}</h3><p className="mt-2 text-3xl font-extrabold">{price}</p>
          <ul className="mt-6 space-y-2">{feats.map((f) => <li key={f} className="flex gap-2 text-sm"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />{f}</li>)}</ul>
        </div>))}</div>
      <div className="card-surface mt-10 p-6 text-sm text-muted-foreground">
        <p className="font-semibold text-foreground">How money moves</p>
        <p className="mt-2">Buyers pay DevMarket in ₹ through our payment gateway partner. For service orders, the seller's earnings (total minus commission) are credited to their DevMarket balance after the buyer accepts the delivery. Project contracts are funded and released milestone by milestone. Digital product earnings are credited when payment is confirmed. Sellers request payouts from their available balance; payouts are processed by our team. DevMarket is not a licensed escrow service.</p>
        <p className="mt-2">Featured listings and promotional placements are managed by admins. Vendor subscription plans are not yet available.</p>
      </div>
    </Wrap>
  );
}

export function About() {
  return (
    <Wrap>
      <PageHeader eyebrow="About" title="A marketplace built for people who build software" />
      <div className="prose-dm mt-10 grid gap-10 lg:grid-cols-2">
        <div><p>DevMarket brings together three ways of working with developers: buying productized services, hiring for custom projects through milestone contracts, and purchasing ready-made digital products like source code, templates and SDKs.</p>
          <p>We keep the rules simple and verifiable: reviews only come from completed orders, paid files are private, and fees are disclosed up front.</p></div>
        <div className="card-surface p-8"><h3 className="mb-3 font-semibold">Principles</h3><ul className="space-y-2 text-sm text-muted-foreground"><li>• No fabricated reviews or metrics</li><li>• Server-side pricing and payment verification</li><li>• Human review for refunds and disputes</li><li>• Demo data is always labelled as such</li></ul></div>
      </div>
    </Wrap>
  );
}

export const COMPANY = {
  name: "XETCX TECHNOLOGY PRIVATE LIMITED",
  cin: "U47912BR2026PTC087622",
  pan: "AAACX6615M",
  address: "C/O K R Singh, F 001 Ramshyam Niketan, Patliputra, Phulwari, Patna, Bihar – 800013, India",
  email: "support@devmarketspace.online",
  phone: "",
  grievance: "Rajesh Pratap Ranjan (Director)",
};

export function CompanyCard() {
  const rows = [["Legal name", COMPANY.name], ["CIN", COMPANY.cin], ["PAN", COMPANY.pan], ["Registered office", COMPANY.address], ["Email", COMPANY.email], ["Grievance officer", COMPANY.grievance], ["Jurisdiction", "Registrar of Companies, Patna"]];
  return (
    <div className="card-surface space-y-4 p-8" data-testid="company-details-card">
      <div className="flex items-center gap-2"><Building2 className="h-5 w-5 text-primary" /><h2 className="font-semibold">Company details</h2></div>
      <dl className="space-y-3 text-sm">{rows.map(([k, v]) => (
        <div key={k}><dt className="text-xs uppercase tracking-wider text-muted-foreground">{k}</dt><dd className="mt-0.5 font-medium" data-testid={`company-${k.toLowerCase().replace(/ /g, "-")}`}>{v}</dd></div>))}</dl>
      <p className="flex items-start gap-2 text-sm text-muted-foreground"><MapPin className="mt-0.5 h-4 w-4 shrink-0" />DevMarket is operated by {COMPANY.name}.</p>
    </div>
  );
}

export function Contact() {
  const api = useApi();
  const [f, setF] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const submit = async (e) => { e.preventDefault(); try { await api.post("/contact", f); setSent(true); } catch (err) { toast.error(errMsg(err)); } };
  return (
    <Wrap>
      <PageHeader eyebrow="Contact" title="Talk to the DevMarket team" text="Messages create a support ticket that our admins answer." />
      <div className="mt-10 grid gap-8 lg:grid-cols-2">
        <CompanyCard />
        <div>
        {sent ? <EmptyState icon={Mail} title="Message received" text="We'll get back to you by email." /> : (
          <form onSubmit={submit} className="card-surface space-y-4 p-8" data-testid="contact-form">
            <Input required placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="contact-name-input" />
            <Input required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="contact-email-input" />
            <Input required placeholder="Subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="contact-subject-input" />
            <Textarea required minLength={10} rows={5} placeholder="How can we help?" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="contact-message-textarea" />
            <Button type="submit" data-testid="contact-submit-button">Send</Button>
          </form>)}
        </div>
      </div>
    </Wrap>
  );
}

const FAQS = [
  ["How do payments work?", "You pay in Indian Rupees through our secure payment gateway (cards, UPI, net banking, wallets). Amounts are calculated on our servers and payment is confirmed with the gateway before your order starts."],
  ["When does the seller get paid?", "Service order earnings are credited to the seller's balance when you accept the delivery. Contract milestones are released individually when you approve them."],
  ["Can I get a refund?", "Request a refund from the order page for paid orders that aren't completed, or within 14 days for digital products. Our team reviews each request."],
  ["What is a dispute?", "If you and the seller can't agree, either party can open a dispute. An admin reviews it and either releases funds, refunds the buyer, or resumes the order."],
  ["How are reviews verified?", "Only buyers with a completed order can leave one review per order."],
  ["What are 'Demo' listings?", "They're sample data to show how the marketplace works. They aren't real sellers and messages to them can't be delivered."],
  ["How do I become a seller?", "Open your dashboard and create a developer or agency storefront. You can then publish services, products and submit proposals."],
  ["How do downloads work?", "Paid files are stored privately. Each download is authorized on our server against your purchase."],
];

export function Help() {
  return (
    <Wrap>
      <PageHeader eyebrow="Help center" title="Frequently asked questions" text="Can't find an answer? Open a support ticket from your dashboard." />
      <Accordion type="single" collapsible className="mt-8 max-w-3xl">{FAQS.map(([q, a], i) => (
        <AccordionItem key={i} value={`q${i}`}><AccordionTrigger data-testid={`help-faq-${i}`}>{q}</AccordionTrigger><AccordionContent className="text-muted-foreground">{a}</AccordionContent></AccordionItem>))}</Accordion>
      <Button asChild className="mt-8" data-testid="help-open-ticket"><Link to="/dashboard/support">Open a support ticket</Link></Button>
    </Wrap>
  );
}

const LEGAL = {
  terms: ["Terms of Service", ["By using DevMarket you agree to use the platform lawfully and to transact only through on-platform payments.", "Sellers are responsible for the services and products they list. DevMarket facilitates payments and charges a disclosed commission.", "We may suspend accounts that violate these terms, including fraud, spam, or IP infringement.", "This template text must be reviewed by legal counsel before production use."]],
  privacy: ["Privacy Policy", ["We store the account details needed to run the marketplace: name, email, profile data, orders and messages.", "Authentication is handled by Clerk; payments are processed by Stripe. We never store card numbers.", "Private files and messages are only accessible to the participants involved and authorized admins.", "This template text must be reviewed by legal counsel before production use."]],
  refunds: ["Refund Policy", ["Paid service orders that are not yet completed can be refunded on request after admin review.", "Digital products can be refunded within 14 days of purchase.", "Approved refunds are issued to the original payment method.", "This template text must be reviewed by legal counsel before production use."]],
};

export function Legal() {
  const { page } = useParams();
  const [t, paras] = LEGAL[page] || LEGAL.terms;
  return <Wrap><PageHeader eyebrow="Legal" title={t} /><div className="prose-dm mt-8 max-w-3xl">{paras.map((p, i) => <p key={i}>{p}</p>)}</div></Wrap>;
}

export function Blog() {
  const api = useApi();
  const [posts, setPosts] = useState(null);
  useEffect(() => { api.get("/blog").then((r) => setPosts(r.data)); }, [api]);
  return (
    <Wrap>
      <PageHeader eyebrow="Blog" title="Notes from DevMarket" />
      {!posts ? <Spinner /> : posts.length === 0 ? <EmptyState title="No posts yet" /> : (
        <div className="mt-10 grid gap-6 md:grid-cols-3">{posts.map((p) => (
          <Link key={p.id} to={`/blog/${p.slug}`} className="card-surface card-hover overflow-hidden" data-testid={`blog-card-${p.slug}`}>
            {p.cover_url && <img src={imgUrl(p.cover_url)} alt="" className="aspect-video w-full object-cover" />}
            <div className="p-6"><p className="text-xs text-muted-foreground">{fmtDate(p.created_at)} · {p.author}</p><h3 className="mt-2 font-semibold">{p.title}</h3><p className="mt-2 text-sm text-muted-foreground">{p.excerpt}</p></div>
          </Link>))}</div>)}
    </Wrap>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const api = useApi();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/blog/${slug}`).then((r) => setP(r.data)).catch(() => setErr(true)); }, [slug, api]);
  if (err) return <Wrap><EmptyState title="Post not found" /></Wrap>;
  if (!p) return <Wrap><Spinner /></Wrap>;
  return (
    <article className="container-x max-w-3xl py-12">
      <p className="eyebrow">{fmtDate(p.created_at)}</p><h1 className="mt-3 text-4xl font-extrabold tracking-tight">{p.title}</h1>
      {p.cover_url && <img src={imgUrl(p.cover_url)} alt="" className="mt-8 aspect-video w-full rounded-xl object-cover" />}
      <div className="prose-dm mt-8">{p.body.split("\n\n").map((x, i) => <p key={i}>{x}</p>)}</div>
    </article>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const api = useApi();
  const sid = params.get("session_id");
  const [st, setSt] = useState({ state: "checking" });
  useEffect(() => {
    let n = 0, stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") return setSt({ state: "paid", order: data.order_id });
        if (data.status === "expired") return setSt({ state: "failed" });
      } catch { /* retry */ }
      if (!stop && ++n < 10) setTimeout(poll, 2000); else setSt((s) => (s.state === "checking" ? { state: "timeout" } : s));
    };
    if (sid) poll();
    return () => { stop = true; };
  }, [sid, api]);
  return (
    <div className="container-x flex min-h-[60vh] items-center py-20">
      <div className="card-surface mx-auto max-w-md p-10" data-testid="payment-status-card">
        {st.state === "checking" && <><Loader2 className="h-8 w-8 animate-spin text-primary" /><h1 className="mt-4 text-2xl font-bold">Confirming payment with Stripe…</h1></>}
        {st.state === "paid" && <><CheckCircle2 className="h-8 w-8 text-emerald-500" /><h1 className="mt-4 text-2xl font-bold" data-testid="payment-success-title">Payment confirmed</h1>
          <p className="mt-2 text-sm text-muted-foreground">Stripe verified your payment. Your order is now active.</p><Button asChild className="mt-6" data-testid="payment-view-order"><Link to={`/orders/${st.order}`}>View order</Link></Button></>}
        {(st.state === "failed" || st.state === "timeout") && <><XCircle className="h-8 w-8 text-rose-500" /><h1 className="mt-4 text-2xl font-bold">{st.state === "failed" ? "Payment not completed" : "Still processing"}</h1>
          <p className="mt-2 text-sm text-muted-foreground">We haven't received confirmation from Stripe yet. Check your orders in a minute.</p><Button asChild className="mt-6" variant="outline"><Link to="/dashboard/orders">My orders</Link></Button></>}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const [params] = useSearchParams();
  return (
    <div className="container-x flex min-h-[60vh] items-center py-20">
      <div className="card-surface mx-auto max-w-md p-10"><XCircle className="h-8 w-8 text-muted-foreground" /><h1 className="mt-4 text-2xl font-bold" data-testid="payment-cancel-title">Checkout cancelled</h1>
        <p className="mt-2 text-sm text-muted-foreground">You weren't charged. You can resume payment from the order page.</p>
        {params.get("order_id") && <Button asChild className="mt-6"><Link to={`/orders/${params.get("order_id")}`}>Back to order</Link></Button>}</div>
    </div>
  );
}
